import { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import { Close as CloseIcon } from '@mui/icons-material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import React from 'react';
import { userTimezoneHeaders } from '../../utils/userTimezoneHeader';

const USE_CASES_API_BASE = process.env.REACT_APP_API_URL_FOR_AI_DETECTION || '';

interface EditUseCaseDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: {
    name: string;
    useCaseType: string;
    zoneType: string;
    accuracy: number;
    description: string;
    modelFile: string;
    imagePreview?: string;
  }) => void;
  /** When true, Save updates local state only (no PUT /use-cases/update). */
  localOnly?: boolean;
  useCase: {
    id: string;
    name: string;
    modelFile: string;
    accuracy: number;
    description: string;
    uploadedOn: Date;
    isFavorite?: boolean;
    imagePreview?: string;
    useCaseType?: string;
    zoneType?: string;
    model_size?: number;
  } | null;
}

export function EditUseCaseDialog({
  open,
  onClose,
  onSave,
  useCase,
  localOnly = false,
}: EditUseCaseDialogProps) {
  const [formData, setFormData] = useState({
    name: '',
    useCaseType: '',
    zoneType: '',
    accuracy: 0,
    description: '',
    modelFile: '',
    imagePreview: '',
  });
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);

  useEffect(() => {
    if (useCase) {
      const base = USE_CASES_API_BASE.replace(/\/$/, '');
      const imagePreview = localOnly
        ? useCase.imagePreview || ''
        : useCase.imagePreview ||
          (base && useCase.id ? `${base}/use-cases/${useCase.id}/image` : '');
      setFormData({
        name: useCase.name,
        useCaseType: useCase.useCaseType || '',
        zoneType: useCase.zoneType || '',
        accuracy: useCase.accuracy,
        description: useCase.description,
        modelFile: useCase.modelFile,
        imagePreview,
      });
      setUpdateError(null);
    }
  }, [useCase, localOnly]);

  const handleClose = useCallback(() => {
    setUpdateError(null);
    onClose();
  }, [onClose]);

  const handleSave = useCallback(async () => {
    if (!useCase || !formData.name.trim() || !formData.description.trim()) return;
    if (localOnly) {
      onSave({
        name: formData.name.trim(),
        useCaseType: formData.useCaseType,
        zoneType: formData.zoneType,
        accuracy: formData.accuracy,
        description: formData.description.trim(),
        modelFile: formData.modelFile,
        imagePreview: formData.imagePreview || undefined,
      });
      handleClose();
      return;
    }
    const base = USE_CASES_API_BASE.trim();
    if (!base) {
      setUpdateError('API base URL is not configured. Add REACT_APP_API_URL_FOR_AI_DETECTION to your .env file.');
      return;
    }
    setIsUpdating(true);
    setUpdateError(null);
    try {
      const zoneType = (formData.zoneType || 'polygon').toLowerCase();
      const body = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        use_case_type: formData.useCaseType || '',
        zone_type: zoneType,
        accuracy: Number(formData.accuracy) || 0,
        model_path: formData.modelFile || '',
        model_size: (useCase as { model_size?: number }).model_size ?? 0,
      };
      const url = `${base.replace(/\/$/, '')}/use-cases/update/${encodeURIComponent(useCase.id)}`;
      const res = await fetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...userTimezoneHeaders() },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        if (res.status === 422 && Array.isArray(data.detail)) {
          const messages = data.detail.map((d: { msg?: string }) => d.msg).filter(Boolean);
          setUpdateError(messages.length ? messages.join(' ') : 'Validation failed.');
        } else {
          setUpdateError(data.message || (typeof data.detail === 'string' ? data.detail : '') || res.statusText || `Update failed (${res.status}).`);
        }
        return;
      }
      onSave({
        name: formData.name.trim(),
        useCaseType: formData.useCaseType,
        zoneType: formData.zoneType,
        accuracy: formData.accuracy,
        description: formData.description.trim(),
        modelFile: formData.modelFile,
        imagePreview: formData.imagePreview || undefined,
      });
      handleClose();
    } catch (err) {
      setUpdateError(err instanceof Error ? err.message : 'Failed to update use case.');
    } finally {
      setIsUpdating(false);
    }
  }, [useCase, formData, onSave, handleClose, localOnly]);

  const isFormValid = formData.name.trim() && formData.description.trim();

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') handleClose();
      }}
      disableEscapeKeyDown
      maxWidth={false}
      PaperProps={{
        sx: {
          width: 700,
          borderRadius: '10px',
          boxShadow: '0px 25px 50px 0px rgba(0,0,0,0.25)',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2,
          borderBottom: '0.8px solid #E5E7EB',
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Roboto, sans-serif',
            fontSize: '18px',
            fontWeight: 600,
            color: '#101828',
          }}
        >
          Edit AI Detection Use Case
        </Typography>
        <IconButton
          size="small"
          onClick={handleClose}
          sx={{
            color: '#99A1AF',
            '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
          }}
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      {/* Content */}
      <Box sx={{ p: 2.5 }}>
        {updateError && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.5,
              p: 1.5,
              mb: 2,
              borderRadius: 1,
              bgcolor: '#FEF2F2',
              border: '1px solid #FECACA',
            }}
          >
            <ErrorOutlineIcon sx={{ color: '#B91C1C', fontSize: 22, flexShrink: 0, mt: 0.25 }} />
            <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#991B1B', fontWeight: 500 }}>
              {updateError}
            </Typography>
          </Box>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {/* Row 1: AI Detection Use Case Name & Use Case Type */}
          <Box sx={{ display: 'flex', gap: 1.25 }}>
            {/* AI Detection Use Case Name */}
            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#364153',
                  mb: 1,
                }}
              >
                AI Detection Use Case Name{' '}
                <span style={{ color: '#FB2C36' }}>*</span>
              </Typography>
              <TextField
                fullWidth
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="PPE Detection"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: 41,
                    fontFamily: 'Arial, sans-serif',
                    fontSize: '14px',
                    color: '#808080',
                    borderRadius: '10px',
                    '& fieldset': {
                      borderColor: '#E5E7EB',
                    },
                  },
                }}
              />
            </Box>

            {/* Use Case Type (Read-only) */}
            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#364153',
                  mb: 1,
                }}
              >
                Use Case Type
              </Typography>
              <TextField
                fullWidth
                value={formData.useCaseType || 'Not specified'}
                disabled
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: 41,
                    fontFamily: 'Arial, sans-serif',
                    fontSize: '14px',
                    color: '#808080',
                    borderRadius: '10px',
                    bgcolor: '#F5F5F5',
                    '& fieldset': {
                      borderColor: '#E5E7EB',
                    },
                  },
                }}
              />
            </Box>
          </Box>

          {/* Row 2: Zone Type & Detection Accuracy (Both Read-only) */}
          <Box sx={{ display: 'flex', gap: 1.25 }}>
            {/* Zone Type (Read-only) */}
            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#364153',
                  mb: 1,
                }}
              >
                Zone Type
              </Typography>
              <TextField
                fullWidth
                value={formData.zoneType || 'Not specified'}
                disabled
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: 41,
                    fontFamily: 'Arial, sans-serif',
                    fontSize: '14px',
                    color: '#808080',
                    borderRadius: '10px',
                    bgcolor: '#F5F5F5',
                    '& fieldset': {
                      borderColor: '#E5E7EB',
                    },
                  },
                }}
              />
            </Box>

            {/* Detection Accuracy (Read-only) */}
            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#364153',
                  mb: 1,
                }}
              >
                Detection Accuracy
              </Typography>
              <TextField
                fullWidth
                value={formData.accuracy ? `${formData.accuracy}%` : 'Not specified'}
                disabled
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: 41,
                    fontFamily: 'Arial, sans-serif',
                    fontSize: '14px',
                    color: '#808080',
                    borderRadius: '10px',
                    bgcolor: '#F5F5F5',
                    '& fieldset': {
                      borderColor: '#E5E7EB',
                    },
                  },
                }}
              />
            </Box>
          </Box>

          {/* Row 3: Description & Image */}
          <Box sx={{ display: 'flex', gap: 1.25 }}>
            {/* Description */}
            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#364153',
                  mb: 1,
                }}
              >
                Description <span style={{ color: '#FB2C36' }}>*</span>
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3.5}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief overview of the use case..."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontFamily: 'Arial, sans-serif',
                    fontSize: '14px',
                    color: '#808080',
                    borderRadius: '10px',
                    '& fieldset': {
                      borderColor: '#E5E7EB',
                    },
                  },
                }}
              />
            </Box>

            {/* Image */}
            <Box sx={{ flex: 0, width: 199 }}>
              <Typography
                sx={{
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#364153',
                  mb: 1,
                }}
              >
                Image
              </Typography>
              <Box
                sx={{
                  width: 199,
                  height: 100,
                  borderRadius: '4px',
                  bgcolor: '#E5E7EB',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  border: '1px solid #E5E7EB',
                }}
              >
                {formData.imagePreview ? (
                  <img
                    src={formData.imagePreview}
                    alt="Preview"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: '#E5E7EB',
                      color: '#6B7280',
                      fontFamily: 'Roboto, sans-serif',
                      fontSize: '14px',
                    }}
                  >
                    No image
                  </Box>
                )}
              </Box>
            </Box>
          </Box>

          {/* Model File (Read-only) */}
          <Box>
            <Typography
              sx={{
                fontFamily: 'Roboto, sans-serif',
                fontSize: '14px',
                fontWeight: 500,
                color: '#364153',
                mb: 1,
              }}
            >
              Model File
            </Typography>
            <TextField
              fullWidth
              value={formData.modelFile}
              disabled
              sx={{
                '& .MuiOutlinedInput-root': {
                  height: 41,
                  fontFamily: 'Arial, sans-serif',
                  fontSize: '14px',
                  color: '#808080',
                  borderRadius: '10px',
                  bgcolor: '#F5F5F5',
                  '& fieldset': {
                    borderColor: '#E5E7EB',
                  },
                },
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 1,
          px: 3,
          py: 2,
          bgcolor: '#F9FAFB',
          borderTop: '0.8px solid #E5E7EB',
          borderBottomLeftRadius: '10px',
          borderBottomRightRadius: '10px',
        }}
      >
        <Button
          onClick={handleClose}
          sx={{
            bgcolor: 'white',
            border: '0.8px solid rgba(0,0,0,0.1)',
            borderRadius: '8px',
            color: '#0A0A0A',
            textTransform: 'none',
            fontFamily: 'Roboto, sans-serif',
            fontSize: '14px',
            fontWeight: 500,
            px: 2,
            py: 1,
            boxShadow: 'none',
            '&:hover': {
              bgcolor: '#F5F5F5',
              boxShadow: 'none',
            },
          }}
        >
          Cancel
        </Button>

        <Button
          onClick={() => handleSave()}
          disabled={!isFormValid || isUpdating}
          sx={{
            bgcolor: '#2932E5',
            border: '0.8px solid #2932E5',
            borderRadius: '8px',
            color: 'white',
            textTransform: 'none',
            fontFamily: 'Roboto, sans-serif',
            fontSize: '14px',
            fontWeight: 500,
            px: 2,
            py: 1,
            boxShadow: 'none',
            '&:hover': {
              bgcolor: '#1f28b8',
              border: '0.8px solid #1f28b8',
              boxShadow: 'none',
            },
            '&:active': {
              bgcolor: '#fff',
              color: '#2932E5',
              borderColor: '#2932E5',
            },
            '&:disabled': {
              bgcolor: '#2932E5',
              color: 'white',
              opacity: 0.5,
            },
          }}
        >
          {isUpdating ? 'Saving…' : 'Save Changes'}
        </Button>
      </Box>
    </Dialog>
  );
}